import { useEffect, useMemo, useState } from "react";
import Table from "examples/Tables/Table";
import { GuestProfileName } from "components/GuestProfileDrawer";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiBadge from "components/VuiBadge";
import VuiButton from "components/VuiButton";
import VuiProgress from "components/VuiProgress";

import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";

import { FaEllipsisV, FaUser, FaClipboardList, FaPlus, FaStickyNote, FaFlag } from "react-icons/fa";
import { HiOutlineBadgeCheck } from "react-icons/hi";
import { FaExclamationTriangle, FaCrown, FaGift, FaNotesMedical } from "react-icons/fa";
import ConciergeRequestsModal from "./ConceirgeRequestDialog";
import AddStaffNoteModal from "./AddNoteDialog";
import ManageGuestFlagModal from "./ManageFlagDialog";
import FlagDetailsDialog from "./FlagDetailDialog";
import GuestProfileDialog from "./GuestProfileDialog";
import useCommon from "hooks/useCommon";
import { getInHouseGuests } from "store/slice/guestSlice";
import dayjs from "utils/dayjs";

import colors from "assets/theme/base/colors";
import borders from "assets/theme/base/borders";
import rgba from "assets/theme/functions/rgba";

import NoResults from "components/NoResult";
import usePermissions from "hooks/usePermissions";
import VuiTooltip from "components/VuiTooltip";
import useHotelDateTime from "hooks/useHotelDateTime";
import { FLAG_CONFIG } from "variables";

const menuItemSX = {
  borderRadius: "8px",
  mx: 0.5,
  my: 0.25,
  transition: "all 0.2s ease",

  "&:hover": {
    backgroundColor: "rgba(0, 76, 127)",
    // background: "linear-gradient(135deg, rgba(0,76,127,0.45), rgba(0,76,127,0.18))",
    // boxShadow: "inset 0 0 0 1px rgba(0,76,127,0.55)",
  },
};

const { black, dark, primary, success, light } = colors;
const { borderWidth } = borders;



const GuestCell = ({ guest, onOpenProfile }) => {
  const initials =
    guest?.name
      ?.split(" ")
      ?.slice(0, 2)
      ?.map((p) => p[0])
      ?.join("")
      ?.toUpperCase() || "G";

  const loyaltyLabel = guest?.loyalty?.badgeLabel || guest?.loyalty?.level;
  const loyaltyColor = guest?.loyalty?.color || "text";

  return (
    <VuiBox display="flex" alignItems="center" gap={1.25}>
      <VuiBox sx={{ cursor: "pointer" }} onClick={onOpenProfile}>
        <Avatar
          src={guest.avatarUrl || ""}
          alt={guest.name}
          sx={{
            width: 32,
            height: 32,
            fontSize: 12,
            color: primary.main,
            background: rgba(primary.main, 0.1),
            border: `${borderWidth[1]} solid ${rgba(primary.main, 0.18)}`,
          }}
        >
          {initials}
        </Avatar>
      </VuiBox>

      <VuiBox
        sx={{ cursor: "pointer" }}
        onClick={onOpenProfile}
        display="flex"
        flexDirection="column"
      >
        <VuiTypography variant="body2" fontWeight="regular" lineHeight={1.2}>
          <GuestProfileName guestId={guest.id} hotelId={guest.hotelId} name={guest.name} guestFlag={guest.guestFlag} guestFlagReason={guest.guestFlagReason} />
        </VuiTypography>

        {loyaltyLabel && (
          <VuiBox display="flex" alignItems="center" gap={0.5} mt={1}>
            <HiOutlineBadgeCheck size={13} color="#fff" style={{ opacity: 0.85 }} />

            <VuiTypography variant="caption" fontWeight="regular" lineHeight={1}>
              {loyaltyLabel} Member
            </VuiTypography>
          </VuiBox>
        )}
      </VuiBox>
    </VuiBox>
  );
};

const FlagCell = ({ flag, onClick }) => {
  if (!flag || !flag.type) {
    return (
      <VuiBox
        display="inline-flex"
        alignItems="center"
        gap={0.75}
        px={1}
        py={0.35}
        borderRadius="7px"
        sx={{
          border: `${borderWidth[1]} solid ${rgba(black.main, 0.25)}`,
          background: light.main,
        }}
      >
        <VuiTypography
          variant="caption"
          fontWeight="medium"
          sx={{
            color: primary.main,
            whiteSpace: "nowrap",
          }}
        >
          None
        </VuiTypography>
      </VuiBox>
    );
  }

  const config = FLAG_CONFIG[flag.type];
  if (!config) return "—";

  const Icon = config.icon;

  return (
    <VuiBox
      onClick={onClick}
      display="inline-flex"
      alignItems="center"
      gap={0.75}
      px={1.5}
      py={0.5}
      borderRadius="7px"
      sx={{
        border: `1px solid ${config.color}60`, //  35% opacity
        background: "rgba(255,255,255,0.02)",
        cursor: "pointer",
      }}
    >
      <Icon size={11} color={config.color} />
      <VuiTypography variant="caption" sx={{ color: config.color }} fontWeight="medium">
        {flag.label}
      </VuiTypography>
    </VuiBox>
  );
};

const RoomCell = ({ room }) => (
  <VuiBox
    px={1.5}
    py={0.5}
    borderRadius="9px"
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
    sx={{
      border: `${borderWidth[1]} solid ${rgba(dark.main, 0.25)}`,
    }}
  >
    <VuiTypography variant="caption">{room}</VuiTypography>
  </VuiBox>
);

const DateArrivedCell = ({ date, time }) => (
  <VuiBox display="flex" flexDirection="column" lineHeight={1.5}>
    <VuiTypography variant="caption">{date || "—"}</VuiTypography>
    <VuiTypography variant="caption">{time || ""}</VuiTypography>
  </VuiBox>
);

const StaffNotesCell = ({ notes = [], onOpenNotes }) => {
  const count = notes.length;
  const hasNotes = count > 0;

  return (
    <VuiBox display="flex" justifyContent="center">
      <VuiBox
        onClick={hasNotes ? onOpenNotes : undefined}
        display="inline-flex"
        alignItems="center"
        gap={0.5}
        px={1.1}
        py={0.4}
        borderRadius="8px"
        sx={{
          cursor: hasNotes ? "pointer" : "default",
          background: hasNotes ? light.secondary : light.main,
          border: hasNotes
            ? `1px solid ${rgba(dark.main, 0.25)}`
            : `1px solid ${rgba(dark.main, 0.15)}`,
          transition: "all 0.2s ease",

          "&:hover": hasNotes
            ? {
                background: dark.main,
              }
            : {},
        }}
      >
        <FaStickyNote size={12} color={hasNotes ? "#ffffff" : "rgba(113, 113, 113, 0.45)"} />

        <VuiTypography
          variant="caption"
          sx={{
            color: hasNotes ? "#ffffff" : "rgba(113, 113, 113, 0.45)",
            minWidth: 10,
            textAlign: "center",
          }}
        >
          {count}
        </VuiTypography>
      </VuiBox>
    </VuiBox>
  );
};

const RequestsCountCell = ({ count = 0, onOpenRequests, canOpenRequests }) => {
  const hasRequests = count > 0;
  const canOpen = hasRequests && canOpenRequests;
  const disabled = hasRequests && !canOpenRequests;

  const content = (
    <VuiBox display="flex" justifyContent="center">
      <VuiBox
        onClick={canOpen ? onOpenRequests : undefined}
        display="inline-flex"
        alignItems="center"
        gap={0.6}
        px={1.25}
        py={0.45}
        borderRadius="8px"
        sx={{
          cursor: canOpen ? "pointer" : disabled ? "not-allowed" : "default",
          background: hasRequests ? light.secondary : light.main,
          border: hasRequests
            ? `1px solid ${rgba(dark.main, 0.25)}`
            : `1px solid ${rgba(dark.main, 0.15)}`,
          opacity: disabled ? 0.55 : 1,
          transition: "all 0.2s ease",

          "&:hover": canOpen
            ? {
                background: dark.main,
              }
            : {},
        }}
      >
        <FaClipboardList size={13} color={hasRequests ? "#ffffff" : "rgba(113, 113, 113, 0.45)"} />

        <VuiTypography
          variant="caption"
          fontWeight="regular"
          sx={{
            color: hasRequests ? "#ffffff" : "rgba(113, 113, 113, 0.45)",
            minWidth: 12,
            textAlign: "center",
          }}
        >
          {count}
        </VuiTypography>
      </VuiBox>
    </VuiBox>
  );

  if (!disabled) return content;

  return (
    <VuiTooltip title="You do not have permission to edit requests." arrow placement="top">
      <span>{content}</span>
    </VuiTooltip>
  );
};

const OccasionCell = ({ occasion }) =>
  occasion ? (
    <VuiBadge variant="gradient" color="success" size="sm" badgeContent={occasion} />
  ) : (
    "—"
  );

const ActionMenuCell = ({
  onOpenConciergeRequest,
  onAddStaffNote,
  onViewProfile,
  onViewRequests,
  onManageFlag,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const { can } = usePermissions();

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const canManageRequests = can("editRequests");

  const run = (fn) => () => {
    handleClose();
    fn?.();
  };

  const menuItemSX = {
    color: dark.main,
    borderRadius: "10px",
    mx: 1,
    my: 0.4,
    px: 2,
    py: 1,

    "& .MuiTypography-root": {
      color: "inherit !important",
    },

    "& svg": {
      color: "inherit !important",
      fill: "currentColor",
    },

    "&:hover": {
      backgroundColor: primary.main,
      color: `${light.main} !important`,

      "& .MuiTypography-root": {
        color: `${light.main} !important`,
      },

      "& svg": {
        color: `${light.main} !important`,
        fill: light.main,
      },
    },

    "&.Mui-disabled": {
      opacity: 0.55,
      color: dark.main,
    },
  };

  return (
    <VuiBox display="flex" justifyContent="center">
      <IconButton
        size="small"
        onClick={handleOpen}
        sx={{
          borderRadius: "10px",
          border: "1px solid rgba(255,255,255,0.18)",
          background: "rgba(255,255,255,0.04)",
        }}
      >
        <FaEllipsisV size={12} color={primary.main} />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 240,
            borderRadius: "16px",

            background: "rgba(255, 255, 255, 0.15)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",

            border: "1px solid rgba(255, 255, 255, 0.18)",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.35)",

            "& .MuiList-root": {
              py: 0.3,
            },
          },
        }}
      >
        <MenuItem onClick={run(onViewProfile)} sx={menuItemSX}>
          <VuiBox display="flex" alignItems="center" gap={1}>
            <FaUser size={12} />

            <VuiTypography variant="button" fontWeight="regular" pl={0.7} sx={{ color: "inherit" }}>
              View Guest Profile
            </VuiTypography>
          </VuiBox>
        </MenuItem>

        {can("editInHouseGuests") && (
          <MenuItem onClick={run(onManageFlag)} sx={menuItemSX}>
            <VuiBox display="flex" alignItems="center" gap={1}>
              <FaFlag size={12} />

              <VuiTypography
                variant="button"
                fontWeight="regular"
                pl={0.7}
                sx={{ color: "inherit" }}
              >
                Manage Flag
              </VuiTypography>
            </VuiBox>
          </MenuItem>
        )}

        {(can("editInHouseGuests") || canManageRequests) && (
          <VuiTooltip
            title={!canManageRequests ? "You do not have permission to edit requests." : ""}
            arrow
            placement="left"
          >
            <span>
              <MenuItem
                onClick={canManageRequests ? run(onOpenConciergeRequest) : undefined}
                disabled={!canManageRequests}
                sx={{
                  ...menuItemSX,
                  cursor: canManageRequests ? "pointer" : "not-allowed",
                }}
              >
                <VuiBox display="flex" alignItems="center" gap={1}>
                  <FaClipboardList size={12} />

                  <VuiTypography
                    variant="button"
                    fontWeight="regular"
                    pl={0.7}
                    sx={{ color: "inherit" }}
                  >
                    Manage Requests
                  </VuiTypography>
                </VuiBox>
              </MenuItem>
            </span>
          </VuiTooltip>
        )}

        {can("editInHouseGuests") && (
          <MenuItem onClick={run(onAddStaffNote)} sx={menuItemSX}>
            <VuiBox display="flex" alignItems="center" gap={1}>
              <FaStickyNote size={12} />

              <VuiTypography
                variant="button"
                fontWeight="regular"
                pl={0.7}
                sx={{ color: "inherit" }}
              >
                Manage Staff Note
              </VuiTypography>
            </VuiBox>
          </MenuItem>
        )}
      </Menu>
    </VuiBox>
  );
};

/* =========================
   TABLE
========================= */

const InHouseGuestsTable = ({ search }) => {
  const { DISPATCH, STORE } = useCommon();
  const { can } = usePermissions();
  const { formatDate, formatTime } = useHotelDateTime();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [selectedGuest, setSelectedGuest] = useState(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [openGuestProfile, setOpenGuestProfile] = useState(false);
  const [openConciergeRequests, setOpenConciergeRequests] = useState(false);
  const [openAddRequest, setOpenAddRequest] = useState(false);
  const [openAddNote, setOpenAddNote] = useState(false);
  const [openManageFlag, setOpenManageFlag] = useState(false);
  const [openFlagDetails, setOpenFlagDetails] = useState(false);

  const [canEditRequests, setCanEditRequests] = useState(false);
  const [canEditNotes, setCanEditNotes] = useState(false);

  const hotelIds = STORE((state) => state.ui.hotelIds);
  const { guests, listLoading, listError, total } = STORE((state) => state.guests);
  const { summaryGenerating } = STORE((state) => state.arrivals);

  const openProfileFor = (guest) => {
    setSelectedGuest(guest);
    setOpenGuestProfile(true);
  };

  const openViewRequestsFor = (guest) => {
    setSelectedGuest(guest);
    setCanEditRequests(false);
    setOpenAddRequest(true);
  };

  const openEditRequestsFor = (guest) => {
    setSelectedGuest(guest);
    setCanEditRequests(true);
    setOpenAddRequest(true);
  };

  const closeRequestsModal = () => {
    setOpenAddRequest(false);
    setCanEditRequests(false);
    setSelectedGuest(null);
  };

  const openAddNoteFor = (guest) => {
    setSelectedGuest(guest);
    setOpenAddNote(true);
  };
  const openViewNotesFor = (guest) => {
    setSelectedGuest(guest);
    setCanEditNotes(false);
    setOpenAddNote(true);
  };

  const openEditNotesFor = (guest) => {
    setSelectedGuest(guest);
    setCanEditNotes(true);
    setOpenAddNote(true);
  };

  const openManageFlagFor = (guest) => {
    setSelectedGuest(guest);
    setOpenManageFlag(true);
  };

  const openFlagDetailsFor = (guest) => {
    setSelectedGuest(guest);
    setOpenFlagDetails(true);
  };

  const handleSaveNote = async (noteText) => {
    setOpenAddNote(false);
  };

  useEffect(() => {
    if (!hotelIds?.length) return;

    const payload = {
      hotelIds,
      page: page + 1,
      pageSize: rowsPerPage,
      sortBy: "arrivedAt",
      sortDir: "desc",
    };

    if (debouncedSearch.length >= 3) {
      payload.search = debouncedSearch;
    }

    DISPATCH(getInHouseGuests(payload));
  }, [hotelIds, page, rowsPerPage, debouncedSearch, DISPATCH]);

  useEffect(() => {
    if (!selectedGuest) return;

    const updated = guests.find((g) => g.reservationId === selectedGuest.reservationId);

    if (updated) {
      setSelectedGuest((prev) => ({
        ...prev,
        ...updated,
        guestSummary:
          typeof updated.aiSummary === "string"
            ? updated.aiSummary
            : updated.aiSummary?.summaryText,
      }));
    }
  }, [guests]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = search?.trim() || "";

      if (trimmed.length >= 3 || trimmed.length === 0) {
        setDebouncedSearch(trimmed);
        setPage(0);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [search]);

  const tableData = useMemo(
    () => ({
      columns: [
        { name: "guest", align: "left" },
        { name: "arrived", align: "center" },
        { name: "flag", align: "center" },
        { name: "room", align: "center" },
        { name: "notes", align: "center" },
        { name: "requests", align: "center" },
        { name: "occasion", align: "center" },
        { name: "actions", align: "center" },
      ],
      rows: (guests || []).map((g) => {
        const mappedGuest = {
          id: g.guestId,
          name: g.guestName,
          badges: g.badges,
          hotelId: g.hotelId,
          reservationId: g.reservationId,

          room: g.roomNumber,

          arrivedDate: g.arrivedAt ? formatDate(g.arrivedAt) : "—",

          arrivedTime: g.arrivedAt ? formatTime(g.arrivedAt) : "",

          requestsCount: g.requestsCount || 0,
          occasion: g.occasion,

          guestFlag: g.guestFlag,
          guestFlagReason: g.guestFlagReason,

          guestSummary: g.aiSummary?.summaryText,

          flag: g.guestFlag
            ? {
                type: g.guestFlag,
                label: FLAG_CONFIG[g.guestFlag]?.label || g.guestFlag,
                reason: g.guestFlagReason,
              }
            : null,

          staffNotes: Array(g.staffNotesCount || 0).fill({}),
        };

        return {
          guest: (
            <GuestCell guest={mappedGuest} onOpenProfile={() => openProfileFor(mappedGuest)} />
          ),
          room: <RoomCell room={mappedGuest.room} />,
          arrived: (
            <DateArrivedCell date={mappedGuest.arrivedDate} time={mappedGuest.arrivedTime} />
          ),
          flag: (
            <FlagCell flag={mappedGuest.flag} onClick={() => openFlagDetailsFor(mappedGuest)} />
          ),
          notes: (
            <StaffNotesCell
              notes={mappedGuest.staffNotes}
              onOpenNotes={() => openViewNotesFor(mappedGuest)}
            />
          ),
          requests: (
            <RequestsCountCell
              count={mappedGuest.requestsCount}
              canOpenRequests={can("editRequests")}
              onOpenRequests={() => openViewRequestsFor(mappedGuest)}
            />
          ),
          occasion: <OccasionCell occasion={mappedGuest.occasion} />,
          actions: (
            <ActionMenuCell
              onOpenConciergeRequest={() => openEditRequestsFor(mappedGuest)}
              onAddStaffNote={() => openEditNotesFor(mappedGuest)}
              onViewProfile={() => openProfileFor(mappedGuest)}
              onManageFlag={() => openManageFlagFor(mappedGuest)}
            />
          ),
        };
      }),
    }),
    [guests]
  );

  return (
    <>
      {!listLoading && (guests || []).length === 0 ? (
        <NoResults
          title={debouncedSearch?.length >= 3 ? "No Matching Results" : "No Results Found"}
          description={
            debouncedSearch?.length >= 3
              ? `No guests found for "${debouncedSearch}".`
              : "There are currently no guests checked in."
          }
        />
      ) : (
        <Table
          columns={tableData.columns}
          rows={tableData.rows}
          pagination
          loading={listLoading}
          paginationMode="client"
          page={page}
          rowsPerPage={rowsPerPage}
          total={tableData.rows.length}
          onPageChange={(newPage) => setPage(newPage)}
          onRowsPerPageChange={(newLimit) => {
            setRowsPerPage(newLimit);
            setPage(0);
          }}
        />
      )}
      <ConciergeRequestsModal
        open={openAddRequest}
        guest={selectedGuest}
        canEdit={canEditRequests}
        onClose={closeRequestsModal}
      />

      <AddStaffNoteModal
        open={openAddNote}
        guest={selectedGuest}
        canEdit={canEditNotes}
        onClose={() => setOpenAddNote(false)}
        onSave={handleSaveNote}
      />

      <ManageGuestFlagModal
        open={openManageFlag}
        guest={selectedGuest}
        onClose={() => setOpenManageFlag(false)}
      />

      <FlagDetailsDialog
        open={openFlagDetails}
        guest={selectedGuest}
        onClose={() => setOpenFlagDetails(false)}
      />

      <GuestProfileDialog
        open={openGuestProfile}
        guest={selectedGuest}
        onClose={() => setOpenGuestProfile(false)}
      />
    </>
  );
};

export default InHouseGuestsTable;
